import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryColorsContext = createContext(null);

export const useCategoryColors = () => {
  const ctx = useContext(CategoryColorsContext);
  if (!ctx) throw new Error('useCategoryColors must be used within CategoryColorsProvider');
  return ctx;
};

const PRESET_PALETTE = [
  '#0052FF', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
  '#06B6D4', '#A855F7', '#DC2626', '#0EA5E9', '#22C55E',
];

export const CategoryColorsProvider = ({ children }) => {
  const [colors, setColors] = useState({});
  const [loaded, setLoaded] = useState(false);

  const fetchColors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories/colors`, { withCredentials: true });
      setColors(response.data || {});
    } catch (e) {
      // User may not be logged in yet — only log in development
      if (process.env.NODE_ENV === 'development') {
        console.debug('CategoryColors fetch skipped:', e.message);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const setCategoryColor = async (category, color) => {
    if (!category || !color) return;
    try {
      const response = await axios.put(
        `${API}/categories/colors`,
        { category, color },
        { withCredentials: true }
      );
      setColors(response.data || {});
    } catch (e) {
      // Toast handled at caller; surface error in dev for debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug('setCategoryColor failed:', e.message);
      }
    }
  };

  const getColor = useCallback((category) => {
    if (!category) return null;
    return colors[category] || null;
  }, [colors]);

  const value = { colors, loaded, getColor, setCategoryColor, presets: PRESET_PALETTE };
  return <CategoryColorsContext.Provider value={value}>{children}</CategoryColorsContext.Provider>;
};
